import React, { useEffect, useState, useCallback } from 'react';
import Dropzone from 'react-dropzone';
import ReactGA from 'react-ga';
import { connect } from 'react-redux';
import { Icon, Message, Segment, Button } from 'semantic-ui-react';
import { fetchEvent, saveHands } from '../../actions';
import { uploadArtifact } from './eventUtils/eventUploadUtils';
import { hasNoEditEvent } from '../helpers/EventUtils';
import { GA_TRACKING_UPLOAD_HANDS_PAGE, uploadTypes } from '../helpers/AppConsts';

const EventUploadHands = ({ edits, fetchEvent, saveHands, match }) => {
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    ReactGA.ga('send', 'pageview', { GA_TRACKING_UPLOAD_HANDS_PAGE });
    fetchEvent(match.params.eventId, match.params.event_organizer);
  }, [fetchEvent, match.params.eventId, match.params.event_organizer]);

  const handleProgress = useCallback((progress) => {
    setUploadStatus(progress.status);
    setUploadMessage(progress.message);
    if (progress.url) {
      setUploadedUrl(progress.url);
    }
  }, []);

  const onDrop = useCallback((files) => {
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setUploadStatus(null);
      setUploadedUrl('');
      uploadArtifact(files, edits.editEvent, saveHands, uploadTypes.HANDS_UPLOAD, handleProgress);
    }
  }, [edits.editEvent, saveHands, handleProgress]);

  const resetUpload = () => {
    setUploadStatus(null);
    setUploadMessage('');
    setUploadedUrl('');
    setSelectedFile(null);
  };

  if (hasNoEditEvent(edits)) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loadingWrapper}>
            <Icon name="spinner" loading size="large" />
            <span style={{ marginLeft: '10px' }}>Loading event details...</span>
          </div>
        </div>
      </div>
    );
  }

  const event = edits.editEvent;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <Icon name="clone" size="large" style={{ marginRight: '10px' }} />
          <h2 style={styles.title}>Upload Hands File</h2>
        </div>

        {/* Event Info */}
        <Segment style={styles.eventInfo}>
          <div style={styles.eventName}>{event.event_name}</div>
          <div style={styles.eventMeta}>
            <span><Icon name="calendar" /> {event.event_day} {event.event_month} {event.event_year}</span>
            <span style={{ marginLeft: '20px' }}><Icon name="map marker" /> {event.event_location}</span>
          </div>
        </Segment>

        {/* Upload Status Messages */}
        {uploadStatus === 'uploading' && (
          <Message icon style={styles.message}>
            <Icon name="circle notched" loading />
            <Message.Content>
              <Message.Header>Uploading</Message.Header>
              {uploadMessage}
            </Message.Content>
          </Message>
        )}

        {uploadStatus === 'success' && (
          <Message positive style={styles.message}>
            <Message.Header>
              <Icon name="check circle" /> Upload Successful
            </Message.Header>
            <p>{uploadMessage}</p>
            {uploadedUrl && (
              <div style={styles.urlContainer}>
                <label style={styles.urlLabel}>File URL:</label>
                <div style={styles.urlBox}>
                  <code style={styles.urlText}>{uploadedUrl}</code>
                  <Button
                    icon="copy"
                    size="small"
                    title="Copy URL"
                    onClick={() => navigator.clipboard.writeText(uploadedUrl)}
                    style={styles.copyButton}
                  />
                </div>
                <a
                  href={uploadedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.viewLink}
                >
                  <Icon name="external" /> View uploaded file
                </a>
              </div>
            )}
            <Button
              style={{ marginTop: '15px' }}
              onClick={resetUpload}
              basic
            >
              Upload Another File
            </Button>
          </Message>
        )}

        {uploadStatus === 'error' && (
          <Message negative style={styles.message}>
            <Message.Header>
              <Icon name="warning circle" /> Upload Failed
            </Message.Header>
            <p>{uploadMessage}</p>
            <Button onClick={resetUpload} basic color="red">
              Try Again
            </Button>
          </Message>
        )}

        {/* Dropzone */}
        {uploadStatus !== 'uploading' && uploadStatus !== 'success' && (
          <Dropzone onDrop={onDrop} accept="text/html,.html,.htm" multiple={false}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps()}
                style={{
                  ...styles.dropzone,
                  ...(isDragActive ? styles.dropzoneActive : {}),
                }}
              >
                <input {...getInputProps()} />
                <Icon name="clone outline" size="huge" style={styles.dropzoneIcon} />
                <p style={styles.dropzoneText}>
                  {isDragActive
                    ? 'Drop the file here...'
                    : 'Drag & drop your hands HTML file here'}
                </p>
                <p style={styles.dropzoneSubtext}>or click to browse</p>
                <p style={styles.dropzoneHint}>Accepted format: .html</p>
              </div>
            )}
          </Dropzone>
        )}

        {/* Selected file info */}
        {selectedFile && uploadStatus !== 'success' && (
          <div style={styles.fileInfo}>
            <Icon name="file" />
            <span>{selectedFile.name}</span>
            <span style={styles.fileSize}>({(selectedFile.size / 1024).toFixed(1)} KB)</span>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: 'calc(100vh - 200px)',
    backgroundColor: '#f5f5f5',
    padding: '40px 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    padding: '30px',
    maxWidth: '600px',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #eee',
  },
  title: {
    margin: 0,
    color: '#333',
    fontSize: '1.5rem',
  },
  eventInfo: {
    backgroundColor: '#f9f9f9',
    marginBottom: '20px',
  },
  eventName: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#569556',
    marginBottom: '8px',
  },
  eventMeta: {
    color: '#666',
    fontSize: '0.9rem',
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#666',
  },
  dropzone: {
    border: '2px dashed #ccc',
    borderRadius: '12px',
    padding: '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#fafafa',
  },
  dropzoneActive: {
    borderColor: '#569556',
    backgroundColor: '#f0f8f0',
  },
  dropzoneIcon: {
    color: '#999',
    marginBottom: '15px',
  },
  dropzoneText: {
    fontSize: '1.1rem',
    color: '#555',
    margin: '0 0 5px 0',
  },
  dropzoneSubtext: {
    color: '#888',
    margin: '0 0 10px 0',
  },
  dropzoneHint: {
    color: '#aaa',
    fontSize: '0.85rem',
    margin: 0,
  },
  message: {
    marginBottom: '20px',
  },
  urlContainer: {
    marginTop: '15px',
  },
  urlLabel: {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333',
  },
  urlBox: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px',
    padding: '10px',
    gap: '10px',
  },
  urlText: {
    flex: 1,
    wordBreak: 'break-all',
    fontSize: '0.85rem',
    color: '#555',
  },
  copyButton: {
    flexShrink: 0,
  },
  viewLink: {
    display: 'inline-block',
    marginTop: '10px',
    color: '#569556',
    textDecoration: 'none',
  },
  fileInfo: {
    marginTop: '15px',
    padding: '10px 15px',
    backgroundColor: '#f0f8f0',
    borderRadius: '6px',
    color: '#555',
  },
  fileSize: {
    marginLeft: '10px',
    color: '#888',
    fontSize: '0.9rem',
  },
};

const mapStateToProps = (state) => {
  return {
    edits: state.edits,
    currentUserEmail: state.auth.userEmail,
  };
};

export default connect(mapStateToProps, { fetchEvent, saveHands })(EventUploadHands);
